import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material'
import { AvailabilityCheckStatus } from 'types/types'
import AvailabilityCheckResult from 'logic/AvailabilityCheckResult'
import { useAPI } from 'contexts/APIProvider'
import { AvailabilityCheckResultForm } from 'types/interfaces'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { DateTime, Duration } from 'luxon'
import APIError from 'errors/APIError'
import AvailabilityCheckCard from './AvailabilityCheckCard'

const NurseAvailabilityCheckList: React.FC = () => {
  const { nurseId } = useParams<{ nurseId: string }>()
  const [searchParams] = useSearchParams()
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const [availabilityChecks, setAvailabilityChecks] = useState<AvailabilityCheckResult[]>([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState<number | null>(null)

  const secret = searchParams.get('secret') || undefined

  const fetchAvailabilityChecks = React.useCallback(async () => {
    if (!nurseId) return
    try {
      setLoading(true)
      const results = await api.listAvailabilityCheckResults({
        nurse_id: parseInt(nurseId),
        secret,
      })
      setAvailabilityChecks(results)
    } catch (err) {
      if ((err as APIError).statusCode === 401) {
        showAlert('error', 'You are not authorized to view this availability check.')
      } else {
        showAlert('error', 'Failed to load availability checks')
      }
      console.error('Error fetching availability checks:', err)
    } finally {
      setLoading(false)
    }
  }, [nurseId, secret, api, showAlert])

  const updateAvailabilityCheck = React.useCallback(async (checkId: number, status: AvailabilityCheckStatus) => {
    try {
      setUpdating(checkId)
      const formData: AvailabilityCheckResultForm = { status }
      await api.updateAvailabilityCheckResult(checkId, formData, secret)
      await fetchAvailabilityChecks()
      showAlert('success', 'Thank you for your response!')
    } catch (err) {
      showAlert('error', 'Failed to update availability check')
      console.error('Error updating availability check:', err)
    } finally {
      setUpdating(null)
    }
  }, [api, showAlert, fetchAvailabilityChecks, secret])

  useEffect(() => {
    fetchAvailabilityChecks()
  }, [fetchAvailabilityChecks])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  const pendingChecks = availabilityChecks.filter(check => check.status === 'pending')
  const respondedChecks = availabilityChecks.filter(check => check.status !== 'pending')

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Availability Check Requests
      </Typography>

      {availabilityChecks.length === 0 ? (
        <Alert severity="info">
          There are no availability check requests for you at the moment. <br />
          The practice pay have cancelled the request. <br />
          We will notify you when there is a new request.
        </Alert>
      ) : (
        <>
          {/* Pending Checks Grid */}
          {pendingChecks.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: 'warning.main' }}>
                Pending Requests
              </Typography>
              <Grid container spacing={2}>
                {pendingChecks.map((check) => (
                  <AvailabilityCheckCard
                    key={check.id}
                    check={check}
                    isPending={true}
                    updating={updating}
                    onUpdateAvailabilityCheck={updateAvailabilityCheck}
                  />
                ))}
              </Grid>
            </Box>
          )}

          {/* Responded Checks Grid */}
          {respondedChecks.length > 0 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary' }}>
                Responded Requests
              </Typography>
              <Grid container spacing={2}>
                {respondedChecks.map((check) => (
                  <AvailabilityCheckCard
                    key={check.id}
                    check={check}
                    isPending={false}
                    updating={updating}
                    onUpdateAvailabilityCheck={updateAvailabilityCheck}
                  />
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default NurseAvailabilityCheckList
